import React, { useMemo } from 'react';
import { Form, Input } from 'antd';
import { Button } from '@/components/ui/Button';
import AppModal from '@/pages/inside/tenant/AppModal';
import { CreateRoleModalBody, CreateRoleModalFooter, ModalBlockLabel } from '@/pages/inside/tenant/style';
import { SendOutlined } from '@ant-design/icons';
import { LINK_URL_EXAMPLE_HELP, normalizeUrlOnFirstType, validateLinkUrlInput } from '@utils/linkUrlValidation.jsx';
import styled from 'styled-components';

const LinkModalFormWrap = styled.div`
    .ant-form-item-explain,
    .ant-form-item-explain-error {
        font-size: 12px;
        line-height: 1.25;
        margin-top: 2px;
    }
`;

export default function AddUrlModal({
    open,
    onCancel,
    form,
    onSubmit,
    title = 'Add link',
    uploadAttachmentsPending = false,
}) {
    const urlValue = Form.useWatch('url', form);

    const urlStartsOk = useMemo(() => {
        const v = typeof urlValue === 'string' ? urlValue.trim() : '';
        if (!v) return false;
        return /^https:\/\//i.test(v);
    }, [urlValue]);

    return (
        <AppModal
            title={title}
            open={open}
            onCancel={onCancel}
            footer={
                <CreateRoleModalFooter $inFooterSlot>
                    <Button
                        $primary={false}
                        $size="14px"
                        onClick={onCancel}
                    >
                        Cancel
                    </Button>
                    <Button
                        $primary
                        $blackOnLight
                        $size="14px"
                        loading={uploadAttachmentsPending}
                        disabled={!urlStartsOk}
                        disabledTooltip={!urlStartsOk ? 'URL must start with "https://" (example: https://example.com)' : undefined}
                        icon={<SendOutlined />}
                        onClick={onSubmit}
                    >
                        {typeof title === 'string' && (title.includes('Attach') || title.includes('URL')) ? 'Attach' : 'Add link'}
                    </Button>
                </CreateRoleModalFooter>
            }
            size="sm"
        >
            <CreateRoleModalBody $flush $noMaxHeight>
                <LinkModalFormWrap style={{ padding: '0 24px 20px 24px' }}>
                    <Form form={form} layout="vertical" requiredMark={false}>
                        <Form.Item noStyle shouldUpdate={(prev, next) => prev?.url !== next?.url}>
                            {() => {
                                const urlErrors = form?.getFieldError?.('url') || [];
                                const showExample = urlErrors.length === 0;
                                return (
                                    <Form.Item
                                        name="url"
                                        label={<ModalBlockLabel>URL</ModalBlockLabel>}
                                        rules={[
                                            {
                                                validator: async (_rule, value) => {
                                                    const check = validateLinkUrlInput(value);
                                                    if (check.ok) return Promise.resolve();
                                                    return Promise.reject(new Error(check.message || 'Enter a valid URL'));
                                                },
                                            },
                                        ]}
                                        extra={showExample ? LINK_URL_EXAMPLE_HELP : null}
                                    >
                                        <Input
                                            placeholder="https://..."
                                            size="large"
                                            onChange={(e) => {
                                                const raw = e?.target?.value ?? '';
                                                const prev = form?.getFieldValue?.('url');
                                                const normalized = normalizeUrlOnFirstType(raw, prev);
                                                if (normalized !== raw) {
                                                    form?.setFieldsValue?.({ url: normalized });
                                                }
                                            }}
                                        />
                                    </Form.Item>
                                );
                            }}
                        </Form.Item>
                        <Form.Item name="title" label={<ModalBlockLabel>Link text</ModalBlockLabel>}>
                            <Input placeholder="Optional display text" size="large" />
                        </Form.Item>
                    </Form>
                </LinkModalFormWrap>
            </CreateRoleModalBody>
        </AppModal>
    );
}

